'use client';

import { type FC, useContext } from 'react';
import { Flex, Tooltip, Text, Counter, Separator } from '@/_components';
import { isEmpty } from '@/_utils/strings';
import {
  getCompletedPaymentCount,
  getTotalPaymentCount,
  getRemainingPaymentCount,
  getDueDate,
} from './PaymentsCountCell.utils';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import type { Voucher, VoucherDefinition } from '@/_types/models/taxReturns';
import type { PaymentsCountCellProps } from './PaymentsCountCell.types';
import styles from './PaymentsCountCell.module.css';

export const PaymentsCountCell: FC<PaymentsCountCellProps> = ({
  taxReturn,
}) => {
  const { voucherDefinitions } = useContext(AdminTaxReturnsContext);
  const completedPaymentCount: number = getCompletedPaymentCount(taxReturn);
  const allPaymentCount: number = getTotalPaymentCount(taxReturn);
  const remainingPaymentCount: number = getRemainingPaymentCount(taxReturn);
  const hasUnpaidStripePayment =
    taxReturn.payment && taxReturn.payment.status !== 'paid';

  const getTooltipContent = (): React.ReactNode => {
    if (allPaymentCount === 0) {
      return <>No tax payments requested.</>;
    }

    if (taxReturn.all_paid && !hasUnpaidStripePayment) {
      return <>All tax payments completed</>;
    }

    const listTitle: string = `${remainingPaymentCount ?? 0} payment${remainingPaymentCount === 1 ? '' : 's'} still needed:`;
    const listItems: Array<React.ReactNode> = [];

    if (hasUnpaidStripePayment) {
      listItems.push(<>• Tax Service Invoice - Due now</>);
    }

    const unpaidVouchers: Array<Voucher> =
      taxReturn?.payment_vouchers?.filter?.(
        (v: any) => !v.is_paid && !v.is_instruction_page,
      ) || [];

    if (!!unpaidVouchers?.length) {
      for (const voucher of unpaidVouchers) {
        const dueDate: string = getDueDate(voucher);

        // with custom payment name
        if (
          voucher.voucher_id === 'other' &&
          !isEmpty(voucher.custom_payment_name)
        ) {
          listItems.push(
            <>
              {voucher.custom_payment_name} - Due date: {dueDate}
            </>,
          );

          // with custom payment name
        } else {
          const voucherName: VoucherDefinition | undefined =
            voucherDefinitions?.find((v: any) => v.id === voucher.voucher_id);

          voucherName &&
            listItems.push(
              <>
                {voucherName?.name} - Due date: {dueDate}
              </>,
            );
        }
      }
    }

    return (
      <Flex isColumn={true} gap={2}>
        {listTitle}
        <Separator isInverted={true} />
        <ul>
          {listItems?.map((item, key) => {
            return (
              <li className={styles.listItem} key={key}>
                {item}
              </li>
            );
          })}
        </ul>
      </Flex>
    );
  };

  return (
    <Tooltip>
      <Tooltip.Content>
        <Text type="tiny" color="currentcolor">
          {getTooltipContent()}
        </Text>
      </Tooltip.Content>
      <Tooltip.Trigger>
        <Counter doneCount={completedPaymentCount} allCount={allPaymentCount} />
      </Tooltip.Trigger>
    </Tooltip>
  );
};
